/**
 * WATCHLIST (Phase 2). The marketplace listings a user is following, in one place.
 *
 * Reads the followed ids from the shared watchlist store and shows the matching listings, live: unheart
 * one here and it leaves the list without a reload. It is a lens over the marketplace, not a separate
 * data source.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { ErrorState, LoadingCard } from '@/components/states';
import { useResource } from '@/lib/use-resource';
import { Stagger, StaggerItem } from '@/components/motion';
import { WatchButton } from '@/components/watch-button';
import { getWatchlist, onWatchlistChange } from '@/lib/watchlist';

const TONE: Record<string, string> = {
  emerald: 'from-emerald-500 to-emerald-700',
  amber: 'from-amber-400 to-amber-600',
  lime: 'from-lime-500 to-lime-700',
  sky: 'from-sky-500 to-sky-700',
  violet: 'from-violet-500 to-violet-700',
  teal: 'from-teal-500 to-teal-700',
};

export default function Watchlist() {
  const { data, loading, error, reload } = useResource(() => api.marketplace(), []);
  const listings = useMemo(() => data?.listings ?? [], [data]);
  const [ids, setIds] = useState<string[]>([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setIds(getWatchlist());
    setReady(true);
    return onWatchlistChange(() => setIds(getWatchlist()));
  }, []);

  const watched = useMemo(() => listings.filter((l) => ids.includes(l.id)), [listings, ids]);

  if (loading)
    return (
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <LoadingCard key={i} lines={2} />
        ))}
      </div>
    );
  if (error) return <ErrorState body="We could not load your watchlist." onRetry={reload} />;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-page-title text-ink">Watchlist</h1>
        <p className="mt-0.5 text-sm text-ink-muted">Assets you are following.</p>
      </header>

      {ready && watched.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border-strong bg-surface p-10 text-center">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-brand">
            <svg
              className="h-6 w-6"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              aria-hidden
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.8}
                d="M12 20s-7-4.35-9.2-8.5C1.3 8.6 2.6 5.5 5.7 5.5c1.9 0 3.2 1.1 4.3 2.6 1.1-1.5 2.4-2.6 4.3-2.6 3.1 0 4.4 3.1 2.9 6-2.2 4.15-9.2 8.5-9.2 8.5Z"
              />
            </svg>
          </span>
          <h2 className="mt-4 font-semibold text-ink">Nothing here yet</h2>
          <p className="mx-auto mt-1 max-w-sm text-sm text-ink-muted">
            Tap the heart on any asset in the marketplace to follow it, and it will show up here.
          </p>
          <Link
            to="/marketplace"
            className="mt-5 inline-flex rounded-field bg-primary px-4 py-2.5 text-sm font-medium text-white shadow-soft transition hover:bg-primary-light"
          >
            Browse the marketplace
          </Link>
        </div>
      ) : (
        <Stagger className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {watched.map((l) => (
            <StaggerItem key={l.id}>
              <Link
                to={`/marketplace/${l.id}`}
                className="group flex h-full flex-col overflow-hidden rounded-card border border-border bg-surface shadow-card transition hover:-translate-y-0.5 hover:shadow-lifted"
              >
                <div
                  className={`relative h-24 bg-gradient-to-br ${TONE[l.tone] ?? TONE.emerald} p-3`}
                >
                  <span className="rounded-pill bg-white/20 px-2 py-0.5 text-micro font-medium text-white backdrop-blur">
                    {l.category}
                  </span>
                  <div className="absolute right-2 top-2">
                    <WatchButton id={l.id} onSurface />
                  </div>
                  <p className="absolute bottom-2 left-3 text-sm font-medium text-white/90">
                    {l.location}
                  </p>
                </div>
                <div className="flex flex-1 flex-col p-4">
                  <h3 className="font-semibold text-ink">{l.title}</h3>
                  <p className="mt-0.5 text-xs text-ink-subtle">Held in {l.trust}</p>
                  <div className="mt-3 flex items-baseline justify-between border-t border-border pt-3">
                    <span className="tabular text-lg font-semibold text-brand">{l.yieldPct}%</span>
                    <span className="text-xs text-ink-muted">{l.fundedPct}% funded</span>
                  </div>
                </div>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      )}
    </div>
  );
}
